import { defineStore } from 'pinia';
import { ref } from 'vue';
import type { Booking, SharedDay, SharedMemberData } from '@gigdriver/domain';
import {
  linkSharedDay as linkApi,
  unlinkSharedDay as unlinkApi,
  watchSharedDays,
  loadSharedMemberData,
  watchSharedMemberBooking,
  getPersonBookings,
  sendSharedOffer as sendSharedOfferApi,
  respondToSharedOffer as respondApi,
  type LinkSharedDayInput,
} from '@gigdriver/client';

// Shared days (Planning/SharedDay.md): live link records for the org, plus the
// other gigs' data a page needs to price a day that spans gigs. The link itself
// is written only by Cloud Functions; sending/answering the combined offer are
// atomic batches in the client package.
export const useSharedDaysStore = defineStore('sharedDays', () => {
  const days = ref<SharedDay[]>([]);
  // Members that live on a gig other than the one being viewed, by booking id.
  const siblings = ref<Map<string, SharedMemberData>>(new Map());
  const busy = ref(false);
  const error = ref<string | null>(null);
  let stop: (() => void) | null = null;
  let loadToken = 0;
  let stopMembers: Array<() => void> = [];

  function watchOrg(orgId: string, onChange?: () => void) {
    stop?.();
    days.value = [];
    siblings.value = new Map();
    stop = watchSharedDays(
      orgId,
      list => {
        days.value = list;
        onChange?.();
      },
      err => {
        error.value = err instanceof Error ? err.message : 'Failed to load shared days';
      },
    );
  }

  function stopWatching() {
    stop?.();
    stop = null;
    stopMembers.forEach(fn => fn());
    stopMembers = [];
    days.value = [];
    siblings.value = new Map();
  }

  const daysForGig = (gigId: string) => days.value.filter(d => d.members.some(m => m.gigId === gigId));
  const dayForBooking = (bookingId: string) => days.value.find(d => d.members.some(m => m.bookingId === bookingId)) ?? null;

  // Fetches the members on OTHER gigs for this gig's shared days. Not live:
  // refreshed whenever the link set changes and on demand (refresh button/after
  // actions); this gig's own members always come from the live gig store.
  async function loadSiblings(orgId: string, gigId: string) {
    const token = ++loadToken;
    const wanted = daysForGig(gigId).flatMap(d => d.members.filter(m => m.gigId !== gigId));
    try {
      const loaded = await Promise.all(wanted.map(async m => [m.bookingId, await loadSharedMemberData(orgId, m)] as const));
      if (token !== loadToken) return;
      const next = new Map<string, SharedMemberData>();
      for (const [id, data] of loaded) if (data) next.set(id, data);
      siblings.value = next;
      // Keep each sibling's booking live; the heavier calls/entries stay as loaded.
      stopMembers.forEach(fn => fn());
      stopMembers = wanted.map(m =>
        watchSharedMemberBooking(orgId, m, booking => {
          const current = siblings.value.get(m.bookingId);
          if (!current || !booking) return;
          const updated = new Map(siblings.value);
          updated.set(m.bookingId, { ...current, booking });
          siblings.value = updated;
        }),
      );
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Failed to load linked gigs';
    }
  }

  async function run<T>(fn: () => Promise<T>): Promise<T> {
    busy.value = true;
    error.value = null;
    try {
      return await fn();
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Shared day action failed';
      throw err;
    } finally {
      busy.value = false;
    }
  }

  return {
    days,
    siblings,
    busy,
    error,
    watchOrg,
    stopWatching,
    daysForGig,
    dayForBooking,
    loadSiblings,
    link: (input: LinkSharedDayInput) => run(() => linkApi(input)),
    unlink: (orgId: string, sharedDayId: string) => run(() => unlinkApi({ orgId, sharedDayId })),
    sendOffer: (members: Booking[]) => run(() => sendSharedOfferApi(members)),
    respond: (members: Booking[], accept: boolean) => run(() => respondApi(members, accept)),
    personBookings: (orgId: string, personId: string) => getPersonBookings(orgId, personId),
  };
});
